import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Trash2, BookMarked, NotebookPen, CheckCircle2, Circle, Route, Compass, Plus, Search, ArrowRight, Boxes } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { ResearchExtractDialog } from "@/components/ai/ResearchExtractDialog";

const PATHWAY_IMG = "https://images.pexels.com/photos/30719635/pexels-photo-30719635.jpeg";

const StatCard = ({ label, value, sub }) => (
  <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
    <div className="text-3xl font-serif text-foreground">{value}</div>
    <div className="text-sm font-medium text-foreground mt-1">{label}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const LearnerView = () => {
  const { user } = useAuth();
  const isCurator = user && (user.role === "curator" || user.role === "admin");
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [catalogueQuery, setCatalogueQuery] = useState("");
  const [extractNote, setExtractNote] = useState(null);
  const catalogueRef = useRef(null);

  const scrollToCatalogue = () => {
    catalogueRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const load = useCallback(async () => {
    const [ov, nt] = await Promise.all([api.get("/learner/overview"), api.get("/notes")]);
    setData(ov.data);
    setNotes(nt.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteNote = async (id) => {
    try {
      await api.delete(`/notes/${id}`);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Note removed");
    } catch { toast.error("Could not delete note"); }
  };

  const toggleEnroll = async (track, join) => {
    try {
      if (join) await api.post(`/learner/paths/${track.id}/enroll`);
      else await api.delete(`/learner/paths/${track.id}/enroll`);
      toast.success(join ? `Joined “${track.title}”` : `Left “${track.title}”`);
      load();
    } catch { toast.error("Could not update your paths"); }
  };

  if (!data) return <div className="max-w-7xl mx-auto px-6 py-16 text-muted-foreground relative z-10">Loading your journey{"\u2026"}</div>;

  const pct = data.total_nodes ? Math.round((data.learned_count / data.total_nodes) * 100) : 0;
  const chartData = [{ name: "progress", value: pct, fill: "hsl(16 60% 49%)" }];
  const enrolledTracks = data.tracks.filter((t) => t.enrolled);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight-serif text-foreground">My Journey</h1>
        <p className="text-muted-foreground mt-1">Track what you have learned and what still awaits.</p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5 mt-8">
        <div className="bg-card border border-border rounded-lg p-5 shadow-sm flex items-center gap-4 lg:col-span-1">
          <div className="w-28 h-28 relative">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={chartData} startAngle={90} endAngle={-270}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar background={{ fill: "hsl(40 30% 92%)" }} dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-serif text-2xl text-foreground">{pct}%</span>
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">Overall progress</div>
            <div className="text-xs text-muted-foreground">{data.learned_count} of {data.total_nodes} entities</div>
          </div>
        </div>
        <StatCard label="Entities learned" value={data.learned_count} sub={`out of ${data.total_nodes}`} />
        <StatCard label="Notes written" value={data.notes_count} sub="thoughts & reflections" />
        <StatCard label="Areas explored" value={data.by_type.filter((c) => c.learned > 0).length} sub={`of ${data.by_type.length} entity types`} />
      </div>

      {/* Category progress */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm mt-6">
        <h2 className="font-serif text-2xl text-foreground mb-4">Progress by entity type</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
          {data.by_type.map((c) => {
            const p = c.total ? Math.round((c.learned / c.total) * 100) : 0;
            return (
              <div key={c.type} data-testid={`category-progress-${c.type}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-foreground">{c.label}</span>
                  <span className="text-muted-foreground">{c.learned}/{c.total}</span>
                </div>
                <Progress value={p} className="h-2" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Paths: enrolled on the left, catalogue on the right */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 mb-4">
            <Route className="w-5 h-5 text-primary" />
            <h2 className="font-serif text-3xl text-foreground tracking-tight-serif">Your Paths</h2>
            {enrolledTracks.length > 0 && (
              <span className="text-sm text-muted-foreground">· {enrolledTracks.length} active</span>
            )}
          </div>

          {enrolledTracks.length === 0 ? (
            <div className="bg-card border border-border border-dashed rounded-lg p-10 text-center" data-testid="choose-path-cta">
              <Compass className="w-10 h-10 mx-auto mb-3 text-primary/60" />
              <h3 className="font-serif text-2xl text-foreground">Choose your path</h3>
              <p className="text-muted-foreground mt-1 max-w-md mx-auto">
                You haven{"\u2019"}t joined any learning paths yet. Browse the catalogue and pick a path to start your journey.
              </p>
              <Button className="mt-5 bg-primary hover:bg-primary/90" data-testid="cta-join-first"
                onClick={scrollToCatalogue}>
                <Compass className="w-4 h-4 mr-2" /> Browse the catalogue
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {enrolledTracks.map((p) => {
                const pp = p.total ? Math.round((p.learned / p.total) * 100) : 0;
                return (
                  <div key={p.id} className="bg-card border border-border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`track-${p.id}`}>
                    <Link to={`/journey/path/${p.id}`} className="block h-24 relative group" data-testid={`open-path-${p.id}`}>
                      <img src={PATHWAY_IMG} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-[#2B221A]/45 group-hover:bg-[#2B221A]/55 transition-colors" />
                      <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between gap-2">
                        <div className="font-serif text-xl text-white leading-tight">{p.title}</div>
                        <button onClick={(e) => { e.preventDefault(); toggleEnroll(p, false); }} data-testid={`leave-path-${p.id}`}
                          className="shrink-0 text-[11px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full px-2 py-0.5 transition-colors">
                          Leave
                        </button>
                      </div>
                    </Link>
                    <div className="p-5">
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{p.description}</p>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-muted-foreground">{p.learned}/{p.total} complete</span>
                        <span className="text-primary font-semibold">{pp}%</span>
                      </div>
                      <Progress value={pp} className="h-2 mb-4" data-testid={`track-progress-${p.id}`} />
                      <ul className="space-y-1.5">
                        {p.nodes.map((n) => (
                          <li key={n.id} className="flex items-center gap-2 text-sm">
                            {n.learned
                              ? <CheckCircle2 className="w-4 h-4 text-accent shrink-0" />
                              : <Circle className="w-4 h-4 text-muted-foreground/50 shrink-0" />}
                            <span className={n.learned ? "text-foreground" : "text-muted-foreground"}>{n.title}</span>
                          </li>
                        ))}
                      </ul>
                      <Link to={`/journey/path/${p.id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid={`open-path-link-${p.id}`}>
                        Open path <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Catalogue */}
        <aside className="lg:col-span-4">
          <div ref={catalogueRef} className="bg-card border border-border rounded-lg shadow-sm lg:sticky lg:top-20 scroll-mt-24" data-testid="path-catalogue">
            <div className="px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Compass className="w-4 h-4 text-primary" />
                <h3 className="font-serif text-lg text-foreground">Path Catalogue</h3>
                <span className="text-xs text-muted-foreground ml-auto">{data.tracks.length} paths</span>
              </div>
              <div className="relative mt-3">
                <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                <Input
                  value={catalogueQuery}
                  onChange={(e) => setCatalogueQuery(e.target.value)}
                  placeholder="Search paths…"
                  className="h-8 pl-8 text-sm"
                  data-testid="catalogue-search"
                />
              </div>
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-border">
              {(() => {
                const fold = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                const q = fold(catalogueQuery.trim());
                const filtered = q
                  ? data.tracks.filter((t) =>
                      fold(t.title).includes(q) ||
                      fold(t.description).includes(q))
                  : data.tracks;
                if (filtered.length === 0) {
                  return (
                    <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="catalogue-empty">
                      No paths match {"\u201C"}{catalogueQuery}{"\u201D"}.
                    </div>
                  );
                }
                return filtered.map((t) => {
                  const tp = t.total ? Math.round((t.learned / t.total) * 100) : 0;
                  return (
                    <div key={t.id} className="px-4 py-3 flex items-start gap-3" data-testid={`catalogue-item-${t.id}`}>
                      <div className="min-w-0 flex-1">
                        <Link to={`/journey/path/${t.id}`} className="font-medium text-sm text-foreground truncate hover:text-primary hover:underline block" data-testid={`catalogue-open-${t.id}`}>{t.title}</Link>
                        <div className="text-[11px] text-muted-foreground">{t.total} entities · {tp}% done</div>
                      </div>
                      {t.enrolled ? (
                        <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-accent" data-testid={`enrolled-badge-${t.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> Enrolled
                        </span>
                      ) : (
                        <Button size="sm" variant="outline" className="shrink-0 h-7 px-2.5 text-xs"
                          onClick={() => toggleEnroll(t, true)} data-testid={`join-path-${t.id}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Join
                        </Button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </aside>
      </div>

      {/* Notes */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <NotebookPen className="w-5 h-5 text-primary" />
          <h2 className="font-serif text-3xl text-foreground tracking-tight-serif">Notes & Thoughts</h2>
        </div>
        {notes.length === 0 ? (
          <div className="bg-card border border-border border-dashed rounded-lg p-10 text-center text-muted-foreground">
            <BookMarked className="w-8 h-8 mx-auto mb-3 opacity-40" />
            No notes yet. Open a concept in Explore and jot your reflections.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {notes.map((n) => (
              <div key={n.id} className="bg-secondary/50 border border-border rounded-lg p-5" data-testid={`note-${n.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="text-xs font-semibold text-accent uppercase tracking-wide">{n.node_title}</div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 -mt-1 -mr-1" onClick={() => deleteNote(n.id)} data-testid={`delete-note-${n.id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </div>
                <p className="text-sm text-foreground mt-2 whitespace-pre-line">{n.text}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="text-[11px] text-muted-foreground">{new Date(n.created_at).toLocaleDateString()}</div>
                  {isCurator && (n.text || "").trim() && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-primary"
                      onClick={() => setExtractNote(n)} data-testid={`extract-note-${n.id}`}>
                      <Boxes className="w-3.5 h-3.5 mr-1" /> Extract entities
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {extractNote && (
        <ResearchExtractDialog
          open={!!extractNote}
          onOpenChange={(o) => !o && setExtractNote(null)}
          text={extractNote.text}
          sourceLabel={`From note (${new Date(extractNote.created_at).toLocaleDateString()})`}
          onCommitted={() => setExtractNote(null)}
        />
      )}
    </div>
  );
};

export default LearnerView;
